import React, { useMemo } from 'react';
import './AlertsPanel.css';

const parseTime = (s) => {
  if (!s) return 0;
  const m = String(s).match(/(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
  if (m) return new Date(`${m[3]}-${m[2]}-${m[1]}`).getTime() || 0;
  return new Date(s).getTime() || 0;
};
const fmtDate = (t) => (t ? new Date(t).toLocaleDateString('he-IL') : '—');

// Change types we monitor for a parcel (icon + label)
const MONITORED = [
  { icon: 'fi-rr-document', text: 'תכנית חדשה או עדכון לתכנית קיימת' },
  { icon: 'fi-rr-bank', text: 'בקשת היתר בנייה חדשה' },
  { icon: 'fi-rr-refresh', text: 'שינוי סטטוס של תכנית או בקשה' },
  { icon: 'fi-rr-snowflake', text: 'הקפאה תכנונית (סעיף 77/78)' },
  { icon: 'fi-rr-shield', text: 'הכרזת שימור על המבנה או האזור' },
];

const STEPS = [
  'אנחנו סורקים את המקורות הרשמיים (עיריות, מינהל התכנון, רשויות מקומיות) מדי יום.',
  'כשמזוהה שינוי שרלוונטי לנכס שלכם — המערכת מאתרת אותו אוטומטית.',
  'מקבלים מייל מיידי עם הפרטים, בלי לבדוק שום דבר ידנית.',
];

export default function AlertsPanel({ permits = [], cityPlans = [], govPlans = [], landUse }) {
  const { events, lastActivity } = useMemo(() => {
    const all = [
      ...permits.map((p) => ({ t: parseTime(p.date), title: p.applicant || `בקשת היתר ${p.num || ''}`.trim(), kind: 'gov-neutral', type: 'בקשת היתר' })),
      ...cityPlans.map((p) => ({ t: parseTime(p.date), title: p.title, kind: 'city', type: 'תכנית עירונית' })),
      ...govPlans.map((p) => ({ t: parseTime(p.date), title: p.title, kind: 'gov', type: 'תכנית ממשלתית' })),
    ].filter((e) => e.title);
    const sorted = all.sort((a, b) => b.t - a.t);
    return { events: sorted.slice(0, 5), lastActivity: sorted[0]?.t || 0 };
  }, [permits, cityPlans, govPlans]);

  const plansCount = cityPlans.length + govPlans.length;
  const permitsCount = permits.length;

  return (
    <div className="alerts-panel">
      {/* Baseline snapshot — bordered 4-cell strip */}
      <section className="alerts-section">
        <div className="alerts-section-title">מה במעקב היום</div>
        <div className="alerts-section-sub">המצב הנוכחי של הנכס — נתריע על כל שינוי ביחס אליו.</div>
        <div className="alerts-strip">
          <div className="alerts-strip-cell"><div className="alerts-strip-n">{plansCount}</div><div className="alerts-strip-l">תוכניות במעקב</div></div>
          <div className="alerts-strip-cell"><div className="alerts-strip-n">{permitsCount}</div><div className="alerts-strip-l">בקשות והיתרים</div></div>
          {landUse && <div className="alerts-strip-cell"><div className="alerts-strip-v">{landUse}</div><div className="alerts-strip-l">ייעוד קרקע</div></div>}
          <div className="alerts-strip-cell"><div className="alerts-strip-v" dir="ltr">{fmtDate(lastActivity)}</div><div className="alerts-strip-l">פעילות אחרונה</div></div>
        </div>
      </section>

      {/* Two columns: what we alert about + how it works */}
      <div className="alerts-cols">
        <section className="alerts-section">
          <div className="alerts-section-head"><i className="fi fi-rr-bell" /><span>על מה נתריע</span></div>
          <div className="alerts-monitored">
            {MONITORED.map((m, i) => (
              <div className="alerts-monitored-row" key={i}>
                <span className="alerts-monitored-ic"><i className={`fi ${m.icon}`} /></span>
                <span className="alerts-monitored-text">{m.text}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="alerts-section">
          <div className="alerts-section-head"><i className="fi fi-rr-bolt" /><span>איך זה עובד</span></div>
          <div className="alerts-steps">
            {STEPS.map((s, i) => (
              <div className="alerts-step" key={i}><span className="alerts-step-n">{i + 1}</span><span>{s}</span></div>
            ))}
          </div>
        </section>
      </div>

      {/* Recent activity */}
      {events.length > 0 && (
        <section className="alerts-section">
          <div className="alerts-section-head"><i className="fi fi-rr-time-past" /><span>פעילות אחרונה בנכס</span></div>
          <div className="alerts-section-sub">זה סוג השינויים שתקבלו עליהם התראה מרגע ההרשמה.</div>
          <div className="alerts-activity">
            {events.map((e, i) => (
              <div className="alerts-activity-row" key={i}>
                <span className="alerts-activity-title" title={e.title}>{e.title}</span>
                <span className={`alerts-activity-badge alerts-activity-badge--${e.kind === 'gov' ? 'gov' : 'city'}`}>{e.type}</span>
                <span className="alerts-activity-date" dir="ltr">{fmtDate(e.t)}</span>
                <span className={`alerts-activity-dot alerts-activity-dot--${e.kind === 'gov' ? 'gov' : 'city'}`} />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
